import { Topic } from '$services/tracking/Topic';
import { type Achievement, AchievementInstructionActionType } from '../../domain/Achievement';
import type { Feature, FeatureStatus } from '@otto-ec/tracking-bct';
import { logger } from '../../logger';
import TrackingService, { ActionName, type TrackingFeature } from '$services/tracking/TrackingService';
import type { ChipSelected } from '$services/tracking/ChipSelected';
import { ActivationResult, ActivationSource } from '$services/tracking/Activation';

export default class AchievementTrackingService {

	private trackingService: TrackingService;

	constructor() {
		this.trackingService = new TrackingService();
	}

	async sendAchievementsCinemaFeatureTracking(
		topic: Topic,
		status: string,
		achievementsMap: Map<string, AchievementTrackingElement>,
		actionName: ActionName | null = null,
		container: Element
	) {
		const trackingFeatures: TrackingFeature[] = [
			{
				id: 'Achievement_Container',
				name: 'AchievementContainer',
				status: status,
				labels: {
					...this.getAggregationFeatureLabels(container)
				},
				position: this.getFeatureOrder(container)
			}
		];

		achievementsMap.forEach((achievementElement: AchievementTrackingElement) =>{
			this.generateTrackingFeaturesFromTrackingAchievementElement([achievementElement], trackingFeatures, status, true);
		});

		const features: Feature[] = this.toTrackingBCTFeature(trackingFeatures);

		await this.trackingService.submitTracking(topic, features, actionName);
	}

	async sendAchievementMiniActionTracking(achievementTileIDs: string[]) {
		const featureIds = achievementTileIDs.map((achievementTileID) => {
			return { 'featureId': achievementTileID };
		});

		await this.trackingService.submitMiniAction(featureIds);
		logger.tracking(Topic.SubmitMiniAction, featureIds);
	};

	async sendVisibleAchievementsFeatureTracking(achievements: HTMLElement[]) {
		const featureIds: { featureId : string }[] = [];
		achievements.forEach((achievement) => {
			const featureId = achievement.getAttribute('data-feature-id');
			if (featureId) {
				featureIds.push({ 'featureId': featureId });
			}
		});
		await this.trackingService.submitMiniAction(featureIds);
		logger.tracking(Topic.SubmitMiniAction, featureIds);
	}

	async sendAchievementsFeatureTracking(
		topic: Topic,
		status: string,
		achievements: Achievement[],
		actionName: ActionName | null = null,
		chipSelected: ChipSelected | null = null
	) {
		const parentLabels = chipSelected ? { "up_AchievementChip": [`${chipSelected}`] } : undefined;

		const trackingFeatures: TrackingFeature[] = this.buildAchievementTrackingFeature(achievements, status, parentLabels);

		const features: Feature[] = this.toTrackingBCTFeature(trackingFeatures);

		await this.trackingService.submitTracking(topic, features, actionName);
	}

	async sendAchievementActionButtonFeatureTracking(achievement: Achievement, actionType: AchievementInstructionActionType) {
		const trackingFeatures: TrackingFeature[] = this.buildAchievementTrackingFeature([achievement], 'clicked');

		if(trackingFeatures[1].labels) {
			let actionValue = '';
			if(actionType === AchievementInstructionActionType.OPEN_SHEET) {
				actionValue = 'open_sheet';
			} else if(actionType === AchievementInstructionActionType.CLOSE) {
				actionValue = 'close';
			} else if(actionType === AchievementInstructionActionType.OPEN_LINK) {
				actionValue = 'open_link';
			}
			if(actionValue){
				trackingFeatures[1].labels.up_AchievementSheetCTA = [actionValue];
			}
		}

		const features: Feature[] = this.toTrackingBCTFeature(trackingFeatures);

		await this.trackingService.submitTracking(Topic.AddActionToEvent, features, ActionName.CLICK);
	};

	async sendAchievementActivationFeatureTracking(
		achievement: Achievement,
		activationSource: ActivationSource,
		activationResult: ActivationResult
	) {
		const trackingFeatures = this.buildAchievementTrackingFeature([achievement], 'clicked');

		if(trackingFeatures[1].labels) {
			const activationLabelValue = `${activationResult}_${activationSource}`;
			trackingFeatures[1].labels.up_AchievementActivation = [activationLabelValue];
		}

		const features: Feature[] = this.toTrackingBCTFeature(trackingFeatures);

		await this.trackingService.submitTracking(Topic.AddActionToEvent, features, ActionName.CLICK);
	}

	async sendAchievementClickFeatureTracking(
		achievementTrackingElement: AchievementTrackingElement,
		cinemaContainer: Element
	) {
		const achievementsMap = new Map<string, AchievementTrackingElement>();
		achievementsMap.set(achievementTrackingElement.achievement.achievementId, achievementTrackingElement);
		await this.sendAchievementsCinemaFeatureTracking(
			Topic.AddActionToEvent,
			'clicked',
			achievementsMap,
			ActionName.OPEN,
			cinemaContainer
		);
	}

	private buildAchievementTrackingFeature(achievement: Achievement[], status: string, parentLabels?: Record<string, string[]>): TrackingFeature[] {
		const trackingFeatures: TrackingFeature[] = [
			{
				'id': 'Achievement_Container',
				'name': 'AchievementContainer',
				'status': status,
				'labels': {
					...(parentLabels ? parentLabels : {})
				}
			}
		];

		this.generateTrackingFeaturesFromAchievements(achievement, trackingFeatures, status);
		return trackingFeatures;
	}

	private generateAchievementTrackingFeaturesGeneric<T>(
		items: T[],
		trackingFeatures: TrackingFeature[],
		status: string,
		isInStorefront: boolean = false,
		getAchievement: (item: T) => Achievement,
		getFeatureId: (item: T, index: number) => string,
		getPosition: (item: T, index: number) => number
	) {
		items.forEach((item, i) => {
			const achievement = getAchievement(item);
			const childPosition = getPosition(item, i);
			const isNewAchievementMechanism = achievement.content && achievement.content.progressStartDate;
			const achievementName = isNewAchievementMechanism
				? "AchievementCard"
				: `${this.getAchievementTrackingName(achievement.content.card.title)}_Tile`;

			const payload: TrackingFeature = {
				'id': getFeatureId(item, i),
				'name': achievementName,
				'parentId': 'Achievement_Container',
				'status': status,
				'labels': {
					'up_AchievementProgress': [`${achievement.progress.current}`],
					'up_AchievementStatus': [`${achievement.progress.state}`],
					'up_AchievementName': [`${this.getAchievementTrackingName(achievement.content.card.title)}`],
					...(this.isSponsored(achievement) ? { 'up_AchievementTag': [`sponsored`] } : {})
				}
			};

			if (isInStorefront) {
				payload.position = childPosition;
			}

			trackingFeatures.push(payload);
		});
	}

	private generateTrackingFeaturesFromAchievements(
		achievements: Achievement[],
		trackingFeatures: TrackingFeature[],
		status: string,
		isInStorefront: boolean = false
	) {
		const getPosition = (item: Achievement, i: number) => {
			return i + 1;
		};

		const getFeatureId = (item: Achievement, index: number) => {
			return `Achievement_Tile_${getPosition(item, index)}`;
		};

		this.generateAchievementTrackingFeaturesGeneric<Achievement>(
			achievements,
			trackingFeatures,
			status,
			isInStorefront,
			(item) => item,
			getFeatureId,
			getPosition
		);
	}

	private generateTrackingFeaturesFromTrackingAchievementElement(
		achievementElements: AchievementTrackingElement[],
		trackingFeatures: TrackingFeature[],
		status: string,
		isInStorefront: boolean = false
	) {

		const getPosition = (item: AchievementTrackingElement, i: number) => {
			const positionAttribute = item.element.getAttribute("data-feature-position");
			return positionAttribute ? parseInt(positionAttribute, 10) : i + 1;
		};

		const getFeatureId = (item: AchievementTrackingElement, i: number) => {
			const attributeFeatureId = item.element.getAttribute("data-feature-id");

			const childPosition = getPosition(item, i);
			const syntheticFeatureId = (childPosition ? `Achievement_Tile_${childPosition}` : `Achievement_Tile_${i + 1}`);

			return attributeFeatureId || syntheticFeatureId;
		};

		this.generateAchievementTrackingFeaturesGeneric<AchievementTrackingElement>(
			achievementElements,
			trackingFeatures,
			status,
			isInStorefront,
			(item) => item.achievement,
			getFeatureId,
			getPosition
		);
	}

	private isSponsored(achievement: Achievement): boolean {
		return achievement.content.sponsor != null && achievement.content.sponsor.trim() !== '';
	}

	private toTrackingBCTFeature(trackingFeatures: {
		id: string,
		name: string,
		status: string,
		parentId?: string,
		labels?: Record<string, string[]>
	}[]): Feature[] {
		return trackingFeatures.map((feature) => {
			return { ...feature, status: feature.status as FeatureStatus };
		});
	}

	private getAggregationFeatureLabels = (container: Element) => {
		const aggregationFeatureLabels = container.parentElement?.parentElement?.parentElement?.parentElement?.getAttribute('data-feature-tracking-labels');

		if (!aggregationFeatureLabels) {
			return {};
		}

		return JSON.parse(aggregationFeatureLabels);
	};

	private getFeatureOrder = (container: Element) => {
		const dataFeatureOrder = container.parentElement?.parentElement?.parentElement?.parentElement?.getAttribute('data-feature-order');
		if (!dataFeatureOrder) {
			return 1;
		}
		return parseInt(dataFeatureOrder, 10);
	};

	private getAchievementTrackingName = (title: string) => {
		return `${title.replaceAll(/[^A-Za-z0-9_\- ]/g, '').trim().replaceAll(' ', '_')}`;
	};
}

export type AchievementTrackingElement = {
	element: Element;
	achievement: Achievement;
};